import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type TeamEntry = {
  teamID: string;
  year: number;
  fullName: string;
  name?: string;
  city?: string;
  abbrev?: string;
};

type GameEntry = {
  gameID: string;
  awayTeamID: string;
  homeTeamID: string;
  awayScore: number | null;
  homeScore: number | null;
  status: string;
  startTimestamp?: string;
  streamingURL?: string;
};

export default function HiddenSpacePage() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<TeamEntry[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("2025");
  const [games, setGames] = useState<GameEntry[]>([]);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now, now - 1, now - 2].map((y) => String(y));
  }, []);

  const {
    data: teamsResponse,
    isLoading: loadingTeams,
    error: teamsError,
  } = useQuery({
    queryKey: ["teams", years],
    queryFn: async (): Promise<TeamEntry[]> => {
      const yearsParam = years.join(",");
      const url = `https://www.backend.ufastats.com/api/v1/teams?years=${yearsParam}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`teams fetch failed: ${res.status}`);
      const json = await res.json();
      const data: TeamEntry[] = json.data || [];
      // dedupe by teamID keeping most recent year
      const byId = new Map<string, TeamEntry>();
      data.forEach((t: TeamEntry) => {
        const existing = byId.get(t.teamID);
        if (!existing || (t.year && t.year > existing.year))
          byId.set(t.teamID, t);
      });
      const unique = Array.from(byId.values()).sort((a, b) =>
        (a.fullName || a.teamID).localeCompare(b.fullName || b.teamID),
      );
      return unique;
    },
  });

  useEffect(() => {
    if (teamsResponse) setTeams(teamsResponse);
    if (teamsResponse && !selectedTeam) {
      const preferred = teamsResponse.find(
        (t) =>
          (t.fullName || "").toLowerCase().includes("oakland") ||
          (t.fullName || "").toLowerCase().includes("spiders"),
      );
      if (preferred) setSelectedTeam(preferred.teamID);
    }
  }, [teamsResponse, selectedTeam]);

  const {
    data: gamesResponse,
    isLoading: loadingGames,
    error: gamesError,
  } = useQuery({
    queryKey: ["games", selectedTeam, selectedYear],
    enabled: !!selectedTeam && !!selectedYear,
    queryFn: async (): Promise<GameEntry[]> => {
      const url = `https://www.backend.ufastats.com/api/v1/games?date=${selectedYear}&teamIDs=${encodeURIComponent(
        selectedTeam,
      )}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`games fetch failed: ${res.status}`);
      const json = await res.json();
      const data: GameEntry[] = json.data || [];
      return data;
    },
  });

  useEffect(() => {
    if (gamesResponse) setGames(gamesResponse);
    else setGames([]);
  }, [gamesResponse]);

  const teamMap = useMemo(() => {
    const m = new Map<string, TeamEntry>();
    teams.forEach((t) => m.set(t.teamID, t));
    return m;
  }, [teams]);

  const handleTeamChange = (e: SelectChangeEvent<string>) => {
    setSelectedTeam(e.target.value as string);
  };
  const handleYearChange = (e: SelectChangeEvent<string>) => {
    setSelectedYear(e.target.value as string);
  };

  const error = teamsError
    ? String(teamsError)
    : gamesError
      ? String(gamesError)
      : null;

  return (
    <Box
      className="min-h-screen p-6"
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Typography variant="h5">UFA: Team Games Viewer</Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel id="year-label">Year</InputLabel>
          <Select
            labelId="year-label"
            value={selectedYear}
            label="Year"
            onChange={handleYearChange}
          >
            <MenuItem value="">(select year)</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 320 }} size="small">
          <InputLabel id="team-label">Team</InputLabel>
          <Select
            labelId="team-label"
            value={selectedTeam}
            label="Team"
            onChange={handleTeamChange}
            renderValue={(v) =>
              v ? teamMap.get(v as string)?.fullName || v : "(select team)"
            }
          >
            <MenuItem value="">(select team)</MenuItem>
            {loadingTeams && (
              <MenuItem disabled>
                <CircularProgress size={16} /> Loading teams...
              </MenuItem>
            )}
            {teams.map((t) => (
              <MenuItem key={t.teamID} value={t.teamID}>
                {t.fullName} {t.abbrev ? `(${t.abbrev})` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Typography color="error">Error: {error}</Typography>}

      <Box>
        {loadingGames ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />{" "}
            <Typography>Loading games...</Typography>
          </Box>
        ) : games.length === 0 ? (
          <Typography>No games to show. Select a team and year.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Opponent</TableCell>
                  <TableCell>Home/Away</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Stream</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {games.map((g) => {
                  const isHome = g.homeTeamID === selectedTeam;
                  const opponentId = isHome ? g.awayTeamID : g.homeTeamID;
                  const opponent =
                    teamMap.get(opponentId)?.fullName || opponentId;
                  const date = g.startTimestamp
                    ? new Date(g.startTimestamp).toLocaleString()
                    : "";
                  const score =
                    g.homeScore == null && g.awayScore == null
                      ? "-"
                      : `${g.homeScore ?? "-"} - ${g.awayScore ?? "-"}`;
                  return (
                    <TableRow
                      key={g.gameID}
                      onClick={() => navigate(`/hiddenspace/game/${g.gameID}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <TableCell>{date}</TableCell>
                      <TableCell>{opponent}</TableCell>
                      <TableCell>{isHome ? "Home" : "Away"}</TableCell>
                      <TableCell>{score}</TableCell>
                      <TableCell>{g.status}</TableCell>
                      <TableCell>
                        {g.streamingURL ? (
                          <Link
                            href={g.streamingURL}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Link
                            <OpenInNewIcon fontSize="small" />
                          </Link>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
